import { Box, keyframes } from '@mui/material';
import React from 'react';

interface InfiniteCarouselProps {
  width: number | string;
  items: Array<JSX.Element>;
  animation_duration: number;
  direction?: 'up' | 'down' | 'left' | 'right';
  height?: number | string;
}

/**
 * Reusable infinite scroller carousel component to showcase an array of elements.
 * @param {number | string} width Width of the outer container.
 * @param {Array<JSX.Element>} items Elements to display across the carousel. NOTE: Total width (or height for vertical direction) of combined elements must be AT LEAST 100% of given outer dimension.
 * @param {number} animation_duration Total time duration for animation to complete in seconds. Lower for faster scrolling speed.
 * @param {'up' | 'down' | 'left' | 'right' | undefined} direction Optionally change the direction of the scrolling. Defaults to left.
 * @param {number | string | undefined} height Optionally provide height of outer container. NOTE: Required if direction = up or down.
 * @returns {JSX.Element}
 */
const InfiniteCarousel = ({
  width,
  items,
  animation_duration,
  direction,
  height
}: InfiniteCarouselProps) => {
  // Duplicate items to allow seamless looping.
  const looping_items: Array<JSX.Element> = [...items, ...items];

  const is_vertical: boolean = direction === 'up' || direction === 'down';
  const is_reveresed: boolean = direction === 'down' || direction === 'right';
  return (
    <Box
      sx={{
        width: width,
        height: height,
        overflow: 'hidden',
        display: is_vertical ? undefined : 'flex',
        alignItems: is_vertical ? undefined : 'center'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: is_vertical ? 'column' : '',
          whiteSpace: 'nowrap',
          width: is_vertical ? width : 'max-content',
          animation: `${keyframes(`
                  from {
                    transform: ${is_vertical ? 'translateY(0%);' : 'translateX(0%);'}
                  }
                  to {
                    transform: ${is_vertical ? 'translateY(-50%);' : 'translateX(-50%)'};
                  }
              `)} ${animation_duration}s linear infinite ${is_reveresed ? 'reverse' : ''}`
        }}
      >
        {looping_items.map((item: JSX.Element, i: number) =>
          React.cloneElement(item, { key: `${item.key}-${i}` })
        )}
      </Box>
    </Box>
  );
};

export default InfiniteCarousel;
